import { Switch, Route, Redirect, useLocation } from "wouter";
import { lazy, Suspense, useEffect } from "react";

import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/components/i18n/I18nProvider";

// Helper per rilevare la lingua del browser
function getBrowserLanguage(): string | null {
  const browserLang = navigator.language || (navigator as any).userLanguage;
  if (!browserLang) return null;
  
  // Estrai solo il codice lingua (es: 'de-DE' -> 'de')
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Restituisci solo se supportato
  if (['it', 'en', 'de', 'sl'].includes(langCode)) {
    return langCode;
  }
  
  // Fallback: se il browser è tedesco ma non riconosciuto
  if (browserLang.toLowerCase().includes('de')) return 'de';
  if (browserLang.toLowerCase().includes('en')) return 'en';
  if (browserLang.toLowerCase().includes('sl')) return 'sl';
  
  // Se la lingua non è italiana o tedesca, usa inglese come fallback
  if (langCode !== 'it' && langCode !== 'de' && langCode !== 'sl') {
    return 'en';
  }
  
  return null;
}
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

// Lazy load pages
const HomePage = lazy(() => import("@/pages/home"));
const ArchivePage = lazy(() => import("@/pages/archive"));
const MapPage = lazy(() => import("@/pages/map"));
const EntitiesPage = lazy(() => import("@/pages/entities"));
const AudioguidesPage = lazy(() => import("@/pages/audioguides"));
const AboutPage = lazy(() => import("@/pages/about"));
const AuthPage = lazy(() => import("@/pages/auth"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const EntityDetailPage = lazy(() => import("@/pages/entity-detail"));
const loadPhotoDetailPage = () => import("@/pages/photo-detail");
const PhotoDetailPage = lazy(loadPhotoDetailPage);
const PrivacyPolicyPage = lazy(() => import("@/pages/privacy-policy"));
const CookiePolicyPage = lazy(() => import("@/pages/cookie-policy"));
const TermsOfServicePage = lazy(() => import("@/pages/terms-of-service"));
const LicensesPage = lazy(() => import("@/pages/licenses"));
const SupportPage = lazy(() => import("@/pages/support"));
const NotFound = lazy(() => import("@/pages/not-found"));
const ItinerariesListPage = lazy(() => import("@/pages/itineraries-list"));
const ItineraryDetailPage = lazy(() => import("@/pages/itinerary-detail"));
const NeighborhoodsListPage = lazy(() => import("@/pages/neighborhoods-list"));
const NeighborhoodDetailPage = lazy(() => import("@/pages/neighborhood-detail"));
const EssaysListPage = lazy(() => import("@/pages/essays-list"));
const EssayDetailPage = lazy(() => import("@/pages/essay-detail"));
const QuizLandingPage = lazy(() => import("@/pages/quiz/index"));
const QuizPlayerPage = lazy(() => import("@/pages/quiz/player"));
const QuizResultPage = lazy(() => import("@/pages/quiz/result"));
const QuizResultPublicPage = lazy(() => import("@/pages/quiz/result-public"));
const QuizBadgePublicPage = lazy(() => import("@/pages/quiz/badge-public"));
const NewsletterConfirmPage = lazy(() => import("@/pages/newsletter-confirm"));
const NewsletterUnsubscribePage = lazy(() => import("@/pages/newsletter-unsubscribe"));

if (typeof window !== "undefined") {
  const isPhotoDetailPath = /^\/(?:(?:it|en|de|sl)\/)?foto\/[^/]+/.test(window.location.pathname);
  if (isPhotoDetailPath) {
    void loadPhotoDetailPage();
  }
}

// Protected/User pages lazy loaded
const Dashboard = lazy(() => import("@/pages/admin/dashboard"));
const ProfilePage = lazy(() => import("@/pages/user/profile"));
const FavoritesPage = lazy(() => import("@/pages/user/favorites/index"));
const UserDashboard = lazy(() => import("@/pages/user/dashboard/index"));
const UserBookmarks = lazy(() => import("@/pages/user/bookmarks"));
const UserBadges = lazy(() => import("@/pages/user/badges"));


import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
const CookieConsentBanner = lazy(() => import("@/components/common/CookieConsentBanner"));
const NewsletterOptInPrompt = lazy(() => import("@/components/modals/NewsletterOptInPrompt"));
import { ProtectedRoute } from "@/lib/protected-route";

import { ChakraBoundary } from "@/components/chakra/ChakraBoundary";
import TranslatedLoadingSpinner from "@/components/common/TranslatedLoadingSpinner";
import { initGTM, setAdminAnalyticsOff } from "./lib/analytics";
import { ROLE } from "@/lib/design-tokens";
import { usePageTracking } from "@/hooks/use-page-tracking";

/**
 * Un componente lazy per rotta, creato una volta sola: `lazy()` chiamato dentro il
 * render produce un tipo nuovo a ogni ciclo, e React smonta e rimonta l'intera
 * pagina admin. Con il rimontaggio si perdono tab attiva, filtri e form aperti a
 * ogni toast, perché il toaster vive sopra le rotte e ne provoca il re-render.
 */
const componentiAdminLazy = new Map<string, React.ComponentType>();

function getComponenteAdminLazy(
  path: string,
  component: () => Promise<{ default: React.ComponentType }>,
): React.ComponentType {
  const esistente = componentiAdminLazy.get(path);
  if (esistente) return esistente;
  const creato = lazy(component);
  componentiAdminLazy.set(path, creato);
  return creato;
}

// Helper per gestire il rendering delle rotte admin protette
function AdminRoute({
  path,
  component
}: {
  path: string;
  component: () => Promise<{ default: React.ComponentType }>;
}) {
  return (
    <Route path={path}>
      {() => {
        const { user, isLoading } = useAuth();

        if (isLoading) {
          return <TranslatedLoadingSpinner fullPage textKey="common.loading" />;
        }

        if (!user || !user.isAdmin) {
          return <Redirect to="/" />;
        }

        const LazyComponent = getComponenteAdminLazy(path, component);
        return (
          <Suspense fallback={<TranslatedLoadingSpinner textKey="common.loading" />}>
            <LazyComponent />
          </Suspense>
        );
      }}
    </Route>
  );
}

function App() {
  // Monitoraggio delle visualizzazioni di pagina
  const [location, navigate] = useLocation();
  const { language, t } = useI18n();
  const { user, isLoading: isLoadingAuth } = useAuth();
  const bgColor = ROLE.pageBg;
  const isQuizLandingPage = /^(?:\/(?:it|en|de|sl))?\/quiz\/?$/.test(location);

  // Controlla se siamo in una pagina admin
  const isAdminPage = location.startsWith('/admin');

  // Inizializza Google Tag Manager al primo caricamento
  useEffect(() => {
    initGTM();
  }, []);

  // Sessione admin: niente tracciamento, anche mentre si naviga il sito pubblico.
  // Finché lo stato auth non è noto non si tocca nulla: i blocchi già attivi
  // (es. rifiuto dei cookie) restano validi.
  useEffect(() => {
    if (isLoadingAuth) return;
    setAdminAnalyticsOff(Boolean(user?.isAdmin));
  }, [isLoadingAuth, user?.isAdmin]);

  // Hook per tracciamento automatico delle pagine
  usePageTracking();

  // Redirect lato client per URL non localizzate e verifica cookie
  useEffect(() => {
    // Skip se siamo in un cambio di lingua attivo
    if ((window as any).__languageChanging) {
      return;
    }
    
    // Skip per route che non devono essere localizzate
    const shouldSkip = 
      location.startsWith('/api') ||
      location.startsWith('/assets') ||
      location.startsWith('/uploads') ||
      location.startsWith('/admin') ||
      location.startsWith('/@') ||
      location.startsWith('/src') ||
      location.startsWith('/node_modules') ||
      location === '/sitemap.xml' ||
      location.match(/\.(js|jsx|ts|tsx|css|png|jpg|jpeg|gif|ico|svg|woff|woff2|map|json)$/);
    
    if (shouldSkip) {
      return;
    }

    // Estrai lingua dall'URL se presente
    const urlLangMatch = location.match(/^\/(it|en|de|sl)(\/|$)/);
    const urlLang = urlLangMatch ? urlLangMatch[1] : null;
    
    // Leggi cookie lingua
    const cookieMatch = document.cookie.match(/preferred_language=([^;]+)/);
    const cookieLang = cookieMatch && ['it', 'en', 'de', 'sl'].includes(cookieMatch[1]) ? cookieMatch[1] : null;
    
    // CASO SPECIALE: Homepage senza lingua - redirect automatico basato su Accept-Language
    if (location === '/' && !urlLang && !cookieLang) {
      const browserLang = getBrowserLanguage();
      if (browserLang && browserLang !== 'it') {
        // Solo se il browser non è italiano, fai redirect
        navigate(`/${browserLang}`, { replace: true });
        return;
      }
    }
    
    // CASO 1: URL non localizzata → redirect alla lingua corretta (cookie o state)
    // ECCEZIONE: Homepage '/' rimane non localizzata se non c'è redirect automatico
    if (!urlLang && location !== '/') {
      const targetLang = cookieLang || language;
      const localizedUrl = `/${targetLang}${location}`;
      navigate(localizedUrl, { replace: true });
      return;
    }
    
    // CASO 2: URL localizzata ma lingua diversa dal cookie → redirect
    // Ma solo se non siamo in un cambio di lingua attivo (evita loop)
    if (urlLang && cookieLang && urlLang !== cookieLang && urlLang !== language) {
      const pathWithoutLang = location.substring(3); // rimuove '/it', '/en', '/de'
      const newUrl = `/${cookieLang}${pathWithoutLang}`;
      navigate(newUrl, { replace: true });
      return;
    }
  }, [location, language, navigate]);

  // Resetta lo scroll ogni volta che cambia la route
  useEffect(() => {
    if (location) {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <TooltipProvider>
      <div className="flex flex-col min-h-screen" style={{ backgroundColor: bgColor }}>
        <a href="#contenuto-principale" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-control focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-ink focus:shadow-lg focus:outline focus:outline-2 focus:outline-focus-ring">{t("common.skipToContent")}</a>
        <Header />
        {/* La home quiz segue l'altezza dei contenuti; il min-height globale aggiungerebbe spazio prima del footer. */}
        <main id="contenuto-principale" tabIndex={-1} className="flex-grow focus:outline-none" style={{ backgroundColor: bgColor, minHeight: isQuizLandingPage ? 0 : undefined }}>
          <ChakraBoundary fallback={<TranslatedLoadingSpinner fullPage textKey="common.loading" />}>
            <Suspense fallback={<TranslatedLoadingSpinner fullPage textKey="common.loading" />}>
              <Switch>
              {/* Admin routes - DEVONO essere prima delle route localizzate */}
              <ProtectedRoute path="/admin" component={Dashboard} adminOnly={true} />
              <AdminRoute path="/admin/photos" component={() => import("@/pages/admin/photos")} />
              <AdminRoute path="/admin/entities" component={() => import("@/pages/admin/entities")} />
              <AdminRoute path="/admin/users" component={() => import("@/pages/admin/users")} />
              <AdminRoute path="/admin/activity" component={() => import("@/pages/admin/activity")} />
              <AdminRoute path="/admin/edit-photo/:id" component={() => import("@/pages/admin/edit-photo")} />
              <AdminRoute path="/admin/settings" component={() => import("@/pages/admin/settings")} />
              <AdminRoute path="/admin/tags" component={() => import("@/pages/admin/tags")} />
              <AdminRoute path="/admin/entity-types" component={() => import("@/pages/admin/entity-types")} />
              <AdminRoute path="/admin/buildings" component={() => import("@/pages/admin/buildings")} />
              <AdminRoute path="/admin/api-keys" component={() => import("@/pages/admin/api-keys")} />
              <AdminRoute path="/admin/default-covers" component={() => import("@/pages/admin/default-covers")} />
              <AdminRoute path="/admin/homepage" component={() => import("@/pages/admin/homepage-settings")} />
              <AdminRoute path="/admin/conversion-modal" component={() => import("@/pages/admin/conversion-modal-settings")} />
              <AdminRoute path="/admin/itineraries/:id" component={() => import("@/pages/admin/itinerary-detail")} />
              <AdminRoute path="/admin/itineraries" component={() => import("@/pages/admin/itineraries")} />
              <AdminRoute path="/admin/rioni/:id/preview" component={() => import("@/pages/neighborhood-detail")} />
              <AdminRoute path="/admin/rioni/:id" component={() => import("@/pages/admin/neighborhood-detail")} />
              <AdminRoute path="/admin/rioni" component={() => import("@/pages/admin/neighborhoods")} />

              <AdminRoute path="/admin/approfondimenti/categorie" component={() => import("@/pages/admin/essay-categories")} />
              <AdminRoute path="/admin/approfondimenti/new" component={() => import("@/pages/admin/essay-editor")} />
              <AdminRoute path="/admin/approfondimenti/:id" component={() => import("@/pages/admin/essay-editor")} />
              <AdminRoute path="/admin/approfondimenti" component={() => import("@/pages/admin/essays")} />
              <AdminRoute path="/admin/quiz" component={() => import("@/pages/admin/quiz")} />
              <AdminRoute path="/admin/newsletter" component={() => import("@/pages/admin/newsletter")} />

              <Route path="/" component={HomePage} />
              <Route path="/:lang(it|en|de|sl)/" component={HomePage} />
              
              {/* Routes con supporto prefissi lingua opzionali */}
              <Route path="/catalog">{() => <Redirect to="/archivio" />}</Route>
              <Route path="/:lang(it|en|de|sl)/catalog">
                {(params: any) => <Redirect to={`/${params.lang}/archivio`} />}
              </Route>
              <Route path="/archivio" component={ArchivePage} />
              <Route path="/:lang(it|en|de|sl)/archivio" component={ArchivePage} />

              {/* Itinerari */}
              <Route path="/rioni/:slug" component={NeighborhoodDetailPage} />
              <Route path="/:lang(it|en|de|sl)/rioni/:slug" component={NeighborhoodDetailPage} />
              <Route path="/rioni" component={NeighborhoodsListPage} />
              <Route path="/:lang(it|en|de|sl)/rioni" component={NeighborhoodsListPage} />
              <Route path="/itinerari/:slug" component={ItineraryDetailPage} />
              <Route path="/:lang(it|en|de|sl)/itinerari/:slug" component={ItineraryDetailPage} />
              <Route path="/itinerari" component={ItinerariesListPage} />
              <Route path="/:lang(it|en|de|sl)/itinerari" component={ItinerariesListPage} />

              <Route path="/approfondimenti/:slug" component={EssayDetailPage} />
              <Route path="/:lang(it|en|de|sl)/approfondimenti/:slug" component={EssayDetailPage} />
              <Route path="/approfondimenti" component={EssaysListPage} />
              <Route path="/:lang(it|en|de|sl)/approfondimenti" component={EssaysListPage} />

              {/* Deprecated: /search -> redirect to /archivio (preserve/migrate query params) */}
              <Route path="/search">
                {() => {
                  const params = new URLSearchParams(window.location.search.substring(1));
                  const out = new URLSearchParams();
                  // migrate q -> search
                  const q = params.get('q') || '';
                  if (q) out.set('search', q);
                  // tags, entityTypeCode, entityId passthrough
                  const tags = params.get('tags') || '';
                  if (tags) out.set('tags', tags);
                  const entityTypeCode = params.get('entityTypeCode') || '';
                  if (entityTypeCode) out.set('entityTypeCode', entityTypeCode);
                  const entityId = params.get('entityId') || '';
                  if (entityId) out.set('entityId', entityId);
                  // year/yearRange -> period (supports any "from-to", not only presets)
                  const year = params.get('year');
                  const yearRange = params.get('yearRange');
                  if (year && !isNaN(parseInt(year, 10))) {
                    const y = parseInt(year, 10);
                    out.set('period', `${y}-${y}`);
                  } else if (yearRange) {
                    const parts = yearRange.split(',').map(s => parseInt(s, 10));
                    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                      out.set('period', `${parts[0]}-${parts[1]}`);
                    }
                  }
                  const target = out.toString() ? `/archivio?${out.toString()}` : '/archivio';
                  return <Redirect to={target} />;
                }}
              </Route>
              <Route path="/:lang(it|en|de|sl)/search">
                {({ lang }: any) => {
                  const params = new URLSearchParams(window.location.search.substring(1));
                  const out = new URLSearchParams();
                  const q = params.get('q') || '';
                  if (q) out.set('search', q);
                  const tags = params.get('tags') || '';
                  if (tags) out.set('tags', tags);
                  const entityTypeCode = params.get('entityTypeCode') || '';
                  if (entityTypeCode) out.set('entityTypeCode', entityTypeCode);
                  const entityId = params.get('entityId') || '';
                  if (entityId) out.set('entityId', entityId);
                  const year = params.get('year');
                  const yearRange = params.get('yearRange');
                  if (year && !isNaN(parseInt(year, 10))) {
                    const y = parseInt(year, 10);
                    out.set('period', `${y}-${y}`);
                  } else if (yearRange) {
                    const parts = yearRange.split(',').map(s => parseInt(s, 10));
                    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                      out.set('period', `${parts[0]}-${parts[1]}`);
                    }
                  }
                  const qs = out.toString();
                  return <Redirect to={`/${lang}/archivio${qs ? `?${qs}` : ''}`} />;
                }}
              </Route>
              
              <Route path="/map" component={MapPage} />
              <Route path="/map/explore" component={MapPage} />
              <Route path="/:lang(it|en|de|sl)/map" component={MapPage} />
              <Route path="/:lang(it|en|de|sl)/map/explore" component={MapPage} />
              
              <Route path="/mappa-edifici">{() => <Redirect to="/map?view=buildings" />}</Route>
              <Route path="/:lang(it|en|de|sl)/mappa-edifici">
                {(params: any) => <Redirect to={`/${params.lang}/map?view=buildings`} />}
              </Route>
              
              <Route path="/schede/audioguide" component={AudioguidesPage} />
              <Route path="/:lang(it|en|de|sl)/schede/audioguide" component={AudioguidesPage} />
              
              <Route path="/schede" component={EntitiesPage} />
              <Route path="/:lang(it|en|de|sl)/schede" component={EntitiesPage} />
              
              <Route path="/scheda/:slug" component={EntityDetailPage} />
              <Route path="/:lang(it|en|de|sl)/scheda/:slug" component={EntityDetailPage} />

              <Route path="/photos/:id" component={PhotoDetailPage} />
              <Route path="/foto/:slug" component={PhotoDetailPage} />
              <Route path="/:lang(it|en|de|sl)/foto/:slug" component={PhotoDetailPage} />
              
              <Route path="/about" component={AboutPage} />
              <Route path="/:lang(it|en|de|sl)/about" component={AboutPage} />
              <Route path="/informazioni" component={AboutPage} />
              
              <Route path="/privacy" component={PrivacyPolicyPage} />
              <Route path="/:lang(it|en|de|sl)/privacy" component={PrivacyPolicyPage} />
              
              <Route path="/cookies" component={CookiePolicyPage} />
              <Route path="/:lang(it|en|de|sl)/cookies" component={CookiePolicyPage} />
              
              <Route path="/terms" component={TermsOfServicePage} />
              <Route path="/:lang(it|en|de|sl)/terms" component={TermsOfServicePage} />
              
              <Route path="/licenses" component={LicensesPage} />
              <Route path="/:lang(it|en|de|sl)/licenses" component={LicensesPage} />
              <Route path="/sostienici" component={SupportPage} />
              <Route path="/:lang(it|en|de|sl)/sostienici" component={SupportPage} />
              <Route path="/auth" component={AuthPage} />
              <Route path="/:lang(it|en|de|sl)/auth" component={AuthPage} />
              <Route path="/password-dimenticata" component={ForgotPasswordPage} />
              <Route path="/:lang(it|en|de|sl)/password-dimenticata" component={ForgotPasswordPage} />
              <Route path="/reset-password" component={ResetPasswordPage} />
              <Route path="/:lang(it|en|de|sl)/reset-password" component={ResetPasswordPage} />

              {/* Quiz: al lancio esiste solo in italiano, niente rotte /en, /de, /sl */}
              <Route path="/quiz" component={QuizLandingPage} />
              <Route path="/it/quiz" component={QuizLandingPage} />
              {/* Le rotte con segmento fisso stanno prima di /quiz/:levelSlug,
                  altrimenti wouter legge "risultato" come slug di livello */}
              <Route path="/quiz/risultato/:attemptId" component={QuizResultPage} />
              <Route path="/it/quiz/risultato/:attemptId" component={QuizResultPage} />
              <Route path="/quiz/condiviso/:token" component={QuizResultPublicPage} />
              <Route path="/it/quiz/condiviso/:token" component={QuizResultPublicPage} />
              <Route path="/quiz/badge/:token" component={QuizBadgePublicPage} />
              <Route path="/it/quiz/badge/:token" component={QuizBadgePublicPage} />
              <Route path="/quiz/:levelSlug" component={QuizPlayerPage} />
              <Route path="/it/quiz/:levelSlug" component={QuizPlayerPage} />

              {/* Si arriva qui da un link in un'email, già nella lingua dell'iscritto: la rotta senza
                  prefisso basta lato client, ma il middleware server-side i18n-routing.ts redirige
                  comunque ogni URL non prefissato a /:lang/..., quindi serve anche l'alias con prefisso
                  (come /sostienici) per non finire sul 404. */}
              <Route path="/newsletter/conferma" component={NewsletterConfirmPage} />
              <Route path="/:lang(it|en|de|sl)/newsletter/conferma" component={NewsletterConfirmPage} />
              <Route path="/newsletter/disiscrizione" component={NewsletterUnsubscribePage} />
              <Route path="/:lang(it|en|de|sl)/newsletter/disiscrizione" component={NewsletterUnsubscribePage} />

              {/* Protected routes - User area */}
              <ProtectedRoute path="/profile" component={ProfilePage} />
              <ProtectedRoute path="/favorites" component={FavoritesPage} />

              {/* Upload gestito tramite dashboard admin */}

              {/* New user area routes - Localized */}
              <ProtectedRoute path="/:lang(it|en|de|sl)/user/dashboard" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.dashboard" />}>
                  <UserDashboard />
                </Suspense>
              )} />

              <ProtectedRoute path="/:lang(it|en|de|sl)/user/favorites" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.favorites" />}>
                  <FavoritesPage />
                </Suspense>
              )} />

              <ProtectedRoute path="/:lang(it|en|de|sl)/user/bookmarks" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.bookmarks" />}>
                  <UserBookmarks />
                </Suspense>
              )} />

              <ProtectedRoute path="/:lang(it|en|de|sl)/user/badges" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="common.loading" />}>
                  <UserBadges />
                </Suspense>
              )} />

              <ProtectedRoute path="/:lang(it|en|de|sl)/user/profile-page" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.profile" />}>
                  <ProfilePage />
                </Suspense>
              )} />

              {/* Legacy user area routes (without language prefix) - for backward compatibility */}
              <ProtectedRoute path="/user/dashboard" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.dashboard" />}>
                  <UserDashboard />
                </Suspense>
              )} />

              {/* Detail route sarà implementata in futuro */}

              <ProtectedRoute path="/user/favorites" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.favorites" />}>
                  <FavoritesPage />
                </Suspense>
              )} />

              <ProtectedRoute path="/user/bookmarks" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.bookmarks" />}>
                  <UserBookmarks />
                </Suspense>
              )} />

              <ProtectedRoute path="/user/badges" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="common.loading" />}>
                  <UserBadges />
                </Suspense>
              )} />

              <ProtectedRoute path="/user/profile-page" component={() => (
                <Suspense fallback={<TranslatedLoadingSpinner textKey="profile.loading.profile" />}>
                  <ProfilePage />
                </Suspense>
              )} />


              {/* Fallback route */}
              <Route component={NotFound} />
            </Switch>
            </Suspense>
            <CookieConsentBanner />
            <Suspense fallback={null}>
              <NewsletterOptInPrompt />
            </Suspense>
          </ChakraBoundary>
        </main>
        <Footer />
      </div>
      <Toaster />
    </TooltipProvider>
  );
}

export default App;
